import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useStripe } from '@stripe/react-stripe-js'
import { Outlet, Navigate, redirect, useActionData, useMatch, useNavigate, useParams, useSubmit } from 'react-router-dom'

// COMPONENTS
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import CurrencyTextField from '@components/molecules/currency-textfield'
import PaymentMethodCell from '@components/organisims/payment-method-cell'
import Button from '@components/atoms/button'
import Spinner from '@components/atoms/spinner'
import WalletOptions from '@components/molecules/wallet-options'
import GroupProgressBar from '@components/molecules/group-progress-bar'
import CreditBalance from '@components/molecules/credit-balance'
import ErrorModal from '@components/molecules/error-modal'

// ROUTES
import AddCardRoute from './add'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import * as GroupActions from '../../_localStore/actions'
import { max, min } from '@constants'
import { createResponseObj, FailedResponse, isFailedResponse, StripeAPI, VirtualCardAPI } from '@apis'
import { useCurrencyInput } from '@hooks/inputs'
import { priceFormatter, toNum, toPennies } from '@utils/number-helper'
import { useGroupDispatch, useGroupSettings } from '../../_localStore/_hooks'
import { usePaymentMethods } from '@/hooks/tanstack/usePaymentMethods'
import { parsePledge } from '@schemas'
import { isNonEmptyString, isNumber, isObject } from '@utils/type-checkers'
import { createLimitedAction } from '@utils/actions'
import { queryClient } from '@/lib/queryClient'

const Component: FC<{}> = () => {
    const { user: clerkUser } = useUser()
    const submit = useSubmit()
    const { data: paymentMethods = [] } = usePaymentMethods(clerkUser?.id)
    const { groupId } = useParams()
    const navigate = useNavigate()
    const stripe = useStripe()
    const { state } = useGroupSettings()
    const localDispatch = useGroupDispatch()

    const isAdd = useMatch({ path: '/group/pm/add' })
    const { pledgeAmount: storedAmount, paymentMethod, requestBody, useCreditBalance: storedUseCreditBalance } = state
    const [currentPaymentMethod, _] = useState(paymentMethod)
    const [isProcessing, setIsProcessing] = useState(false)
    const processingRef = useRef<HTMLDialogElement>(null)
    const actionData = useActionData()

    const [useCreditBalance, setUseCreditBalance] = useState(storedUseCreditBalance ?? false)
    const hasAutoAppliedCredit = useRef(false)
    const creditAmount = useMemo(() => (clerkUser?.publicMetadata?.creditAmount) || 0, [clerkUser?.publicMetadata?.creditAmount])

    const [showError, setError] = useState(false)
    const [error, setErrorData] = useState<FailedResponse>()

    const confirmZeroRef = useRef<HTMLDialogElement>(null)
    function openConfirmZero() { if (confirmZeroRef.current) confirmZeroRef.current.showModal() }
    function closeConfirmZero() { if (confirmZeroRef.current) confirmZeroRef.current.close() }

    useEffect(() => {
        if (isFailedResponse(actionData)) {
            setErrorData(actionData)
            setError(true)
        }
    }, [actionData])

    const user = useMemo((): User => ({
        id: clerkUser?.id,
        givenName: clerkUser?.firstName,
        familyName: clerkUser?.lastName,
        imageUrl: clerkUser?.imageUrl,
    }), [clerkUser?.id, clerkUser?.firstName, clerkUser?.lastName, clerkUser?.imageUrl])

    const [pledgeAmount, pledgeAmountProps, setPledgeAmount] = useCurrencyInput({
        initValue: `${(storedAmount || 0)}`, max,
        onFocus: () => { if (!!invalid) setInvalid(false) },
        onBlur: (amount) => localDispatch(GroupActions.setPledgeAmount(amount))
    })

    useEffect(() => {
        if (storedUseCreditBalance !== undefined) {
            hasAutoAppliedCredit.current = true
            return
        }

        if (!hasAutoAppliedCredit.current && creditAmount > 0) {
            if (!pledgeAmount || Number(pledgeAmount) === 0) {
                setPledgeAmount(String(creditAmount / 100))
            }
            setUseCreditBalance(true)
            localDispatch(GroupActions.setUseCreditBalance(true))
            hasAutoAppliedCredit.current = true
        }
    }, [creditAmount, localDispatch, pledgeAmount, setPledgeAmount, storedUseCreditBalance])

    // When credit toggle is turned on with no amount entered, auto-fill with credit amount
    function handleCreditToggle(checked: boolean) {
        setUseCreditBalance(checked)
        localDispatch(GroupActions.setUseCreditBalance(checked))
        if (checked && (!pledgeAmount || Number(pledgeAmount) === 0)) {
            // Auto-fill with credit amount in dollars
            setPledgeAmount(String(creditAmount / 100))
        }
    }

    // Determine if payment method is required
    // Payment method is NOT required only if: credit toggle is ON AND credit covers the full amount
    const requiresPaymentMethod = useMemo(() => {
        if (toPennies(pledgeAmount) === 0) return false
        if (useCreditBalance && toPennies(pledgeAmount) <= creditAmount) return false
        return true
    }, [useCreditBalance, pledgeAmount, creditAmount])

    const amountOwed = useMemo(() => {
        if (!useCreditBalance) return toPennies(pledgeAmount)
        return Math.max(0, toPennies(pledgeAmount) - creditAmount)
    }, [useCreditBalance, pledgeAmount, creditAmount])

    const existingPledgesTotal = useMemo(() => {
        return (requestBody.pledges || []).reduce((total, pledge) => total + pledge.amount, 0)
    }, [requestBody.pledges])

    const totalProgressPledge = useMemo(() => {
        return (toPennies(pledgeAmount) + existingPledgesTotal) / 100
    }, [existingPledgesTotal, pledgeAmount])

    const showAmountOwed = useMemo(() => (
        useCreditBalance && toPennies(pledgeAmount) > creditAmount
    ), [useCreditBalance, pledgeAmount, creditAmount])

    const submitable = useMemo(() => (
        toPennies(pledgeAmount) >= ((toPennies(pledgeAmount) > 0 && toPennies(pledgeAmount) < 100) ? min : 0)
        && toPennies(pledgeAmount) <= max
    ), [pledgeAmount])

    const [invalid, setInvalid] = useState<boolean>(false)

    // Handles reseting the local store after a payment method has been deleted from the users pm array.
    useEffect(() => {
        if (paymentMethod && paymentMethod.type === 'credit_card' && !paymentMethods.find(({ token }) => token === paymentMethod.token))
            localDispatch(GroupActions.setPaymentMethod(undefined))
    }, [paymentMethods, paymentMethod])

    // Reset processing state if action returns an error
    useEffect(() => {
        if (actionData && isProcessing) {
            setIsProcessing(false)
            processingRef.current?.close()
        }
    }, [actionData])

    function onPaymentConfirmed(intentId: string, paymentMethod?: CreditCard | ApplePay | GooglePay) {
        const formData = new FormData()
        formData.append('requestBody', JSON.stringify(requestBody))
        formData.append('pledge', String(toPennies(pledgeAmount)))
        formData.append('user', JSON.stringify(user))
        formData.append('intentId', JSON.stringify(intentId))
        formData.append('useCredit', String(useCreditBalance))

        if (paymentMethod)
            formData.append('paymentMethod', JSON.stringify(paymentMethod))

        submit(formData, { method: 'POST' })
    }

    function handlePMSelected(card: CreditCard) {
        if (paymentMethod?.type === 'credit_card' && paymentMethod.token === card.token) localDispatch(GroupActions.setPaymentMethod(undefined))
        else localDispatch(GroupActions.setPaymentMethod(card))
    }

    async function handleCreditCardPayment() {
        const amount = toPennies(pledgeAmount)

        localDispatch(GroupActions.setPledgeAmount(amount / 100))

        if (!submitable) {
            throw new Error(`Pledge Amounts must be more than $1`)
        }

        // If amount is 0 and no payment method, skip payment setup
        if (amount === 0 && !paymentMethod) {
            onPaymentConfirmed('')
            return
        }

        if (paymentMethod?.type === 'credit_card') {
            setIsProcessing(true)
            processingRef.current?.showModal()
            try {
                const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch()
                const { setupIntent, error } = await stripe?.confirmCardSetup(clientSecret, {
                    payment_method: paymentMethod.token
                }) || {}

                if (!setupIntent || error) throw error || new Error("Failed to generate Setup intent")
                processingRef.current?.close()
                onPaymentConfirmed(setupIntent.id, paymentMethod)
            } catch (error) {
                setIsProcessing(false)
                processingRef.current?.close()
                console.error('Stripe setup intent error:', error)
                throw error
            }
        }
    }

    function handleAdd() {
        navigate('./add')
    }

    function handleBack() {
        navigate(-1)
    }

    if (!requestBody.title) return <Navigate to="../" replace={true} />
    else return (<>
        <Column.Header.Start>
            {!isAdd && <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
            </a>}
        </Column.Header.Start>
        {isAdd && <h2 id='add-card-title' style={{ textAlign: 'center' }}>Add New Payment Method</h2>}
        {(!isAdd) && (<>
            <div className='flex flex-col justify-center gap-4'>
                <h2 style={{ marginTop: '1rem', textAlign: 'center' }}>Enter Your Contribution</h2>
                <GroupProgressBar goal={requestBody.targetAmount || 0} pledge={totalProgressPledge} />
                <div className="flex flex-row items-center" style={{ width: '100%', padding: '0.625rem 1.25rem', gap: 'inherit', backgroundColor: 'var(--splitcard-gray-50)', border: '1px solid var(--splitcard-gray-100)', borderRadius: 'var(--border-radius-md)' }}>
                    <Avatar user={user} />
                    <p className="text-body-2"><strong>{clerkUser?.fullName || 'Me'}</strong></p>
                </div>
                <CurrencyTextField invalid={invalid} centered={true} {...pledgeAmountProps} />
                {creditAmount > 0 && (
                    <CreditBalance
                        style={{ display: 'flex', flexFlow: 'row nowrap' }}
                        creditAmount={creditAmount}
                        isApplied={useCreditBalance}
                        onToggle={handleCreditToggle}
                    />
                )}
                {showAmountOwed && (
                    <div className='flex flex-row justify-between items-center'>
                        <h4>Amount Owed:</h4>
                        <h4>{priceFormatter.format(amountOwed / 100)}</h4>
                    </div>
                )}
            </div>
        </>)}
        {!isAdd && <h2 style={{ marginTop: '1rem', textAlign: 'center' }}>Payment Method</h2>}
        {(isAdd || currentPaymentMethod === undefined || currentPaymentMethod?.type === 'credit_card') ? null :
            <PaymentMethodCell
                selected={paymentMethod?.type !== 'credit_card'}
                paymentMethod={currentPaymentMethod}
            />
        }
        <PaymentMethodCell type={isAdd ? 'empty' : 'add'} onClick={!isAdd ? handleAdd : undefined}>
            <Outlet />
        </PaymentMethodCell>
        {isAdd ? null : paymentMethods.map((method, index) => (
            <PaymentMethodCell key={`${method.token}-${index}`}
                selected={paymentMethod?.type === 'credit_card' && method.token === paymentMethod?.token}
                onClick={() => handlePMSelected(method)}
                paymentMethod={method}
                removable
            />
        ))}
        {(!isAdd) &&
            <WalletOptions
                groupId={groupId || ''}
                amount={amountOwed}
                onConfirm={onPaymentConfirmed} enterZero={true}
            />
        }
        {isAdd ? null : (
            <Column.Footer sticky>
                <Button onClick={!pledgeAmount ? openConfirmZero : handleCreditCardPayment} disabled={!submitable || isProcessing || (requiresPaymentMethod && !paymentMethod)}>Create Group</Button>
            </Column.Footer>
        )}
        <dialog ref={processingRef}>
            <div>
                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
        <dialog ref={confirmZeroRef}>
            <div>
                <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <h2>Wait!</h2>
                    <p>You are attempting to create a group with no initial contribution. Are you sure?</p>
                    <div className="flex flex-col items-stretch gap-2.5">
                        <div className='flex flex-row gap-2.5'>
                            <Button theme="secondary" style={{ flex: 1 }} formMethod="dialog" onClick={closeConfirmZero}>Cancel</Button>
                            <Button theme="primary" style={{ flex: 1 }} type="submit" onClick={handleCreditCardPayment}>Confirm</Button>
                        </div>
                    </div>
                </div>
            </div>
        </dialog>
        <ErrorModal
            state={[showError, setError]}
            error={error}
            getTitle={(modalError) => {
                if (modalError?.name === 'ReloadableCardAlreadyLinkedError') return 'Reloadable Card Already In Use'
                if (modalError?.name === 'PhysicalCardAlreadyLinkedError') return 'Physical Card Already In Use'
                return 'Wait!'
            }}
            getMessage={(modalError) => {
                if (modalError?.name === 'ReloadableCardAlreadyLinkedError') return 'Your reloadable card is already in use. Either close your current group linked to this card or use a single use card.'
                if (modalError?.name === 'PhysicalCardAlreadyLinkedError') return 'Your physical card is already in use. Either close your current group linked to this card or use a single use card.'
                return modalError?.message || 'We encountered an issue creating your group. If this keeps happening please contact support at 888-625-4349'
            }}
        />
    </>)
}


const action = createLimitedAction({
    limit: 'createGroups',
    getRedirectPath: ({ params }) => isNonEmptyString(params.groupId) ? `/group/${params.groupId}` : '/account',
}, async ({ request }) => {
    try {
        const formData = await request.formData()
        const requestBody = JSON.parse(`${formData.get('requestBody')}`),
            pledge = toNum(`${formData.get("pledge") || ''}`),
            user = JSON.parse(formData.get("user") as string),
            useCredit = formData.get('useCredit') === 'true'

        var paymentMethod = formData.get("paymentMethod") !== null
            ? JSON.parse(formData.get("paymentMethod") as string)
            : undefined

        // Create Group
        console.log("Create Group Data:", requestBody)
        if (!isObject<CreateGroupRequestBody>(requestBody)
            || (!isNonEmptyString(requestBody.title) || requestBody.title.length > 50)
            || (requestBody.targetAmount !== undefined && !isNumber(requestBody.targetAmount))
        ) throw new Error(`Missing required resources. ${JSON.stringify(requestBody)}`)

        // Organizer Pledge Data
        console.log("Organizer Pledge Data:", { pledge, user, intent })
        if (pledge < ((pledge > 0 && pledge < 100) ? min : 0) || !isObject<User>(user) || (paymentMethod && !isObject<CreditCard>(paymentMethod))) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ pledge, user, paymentMethod, intent })}`
        })

        var intent = JSON.parse(formData.get("intentId") as string);
        // Only require intent if pledge amount > 0
        if (pledge > 0 && !isNonEmptyString(intent)) {
            throw createResponseObj({
                status: 'failed', type: 'local', name: 'missing-resources',
                message: `Missing required resources. ${JSON.stringify({ pledge, user, paymentMethod, intent })}`
            })
        }

        const group: CreateGroupRequestBody = {
            ...requestBody,
            pledges: [parsePledge({
                amount: pledge,
                paymentMethod,
                transactionId: intent,
                user,
                userId: user.id,
                useCredit,
            }), ...(requestBody.pledges || [])]
        }

        const groupId = await VirtualCardAPI.CreateGroupRequest.fetch(group)
        const isRecreateFlow = new URL(request.url).pathname.includes('/group/recreate/')
        queryClient.invalidateQueries({ queryKey: ['user-groups'] })
        sessionStorage.setItem('showNotificationPrompt', 'true')
        return redirect(`/group/${groupId}/card-ready${isRecreateFlow ? '?fromRecreate=true' : ''}`)
    } catch (error) {
        console.error(error)
        return error
    }
})

const PMRoute: RouteObject = {
    path: "pm",
    Component,
    action,
    children: [
        AddCardRoute,
    ],
}

export { PMRoute }
